import { computed, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';
import store from '@/store';

/**
 * useListaSelection — Centraliza el estado y navegación de la temporada activa
 * para la sección bases/deportistas.
 *
 * Provee el listaId desde la URL, la listaActiva resuelta desde el store,
 * y cambiarLista() que mantiene al usuario en la misma sub-página al cambiar
 * de temporada.
 *
 * IMPORTANTE: Este composable accede a la ruta via `vm.proxy.$route` en
 * cada lectura, NO a través de la variable `route` capturada de useRoute().
 * En este proyecto, useRoute() devuelve un snapshot del $route en el momento
 * de la llamada, no una referencia reactiva. Si capturáramos `route`, todos
 * los computed basados en route.params devolverían el valor INICIAL y nunca
 * se actualizarían al navegar. Ver vue-best-practices / router/useRouter.js
 * para más contexto.
 *
 * @returns {{ listaId: import('vue').ComputedRef<string|undefined>, listaActiva: import('vue').ComputedRef<object|null>, listas: import('vue').ComputedRef<Array>, cambiarLista: Function }}
 */
export function useListaSelection() {
    const route = useRoute();
    const router = useRouter();
    const vm = getCurrentInstance();

    /**
     * Helper: devuelve SIEMPRE el $route actual del instance.
     * NO uses la variable `route` capturada — es un snapshot.
     * Fallback a `route` cuando getCurrentInstance() retorna null
     * (p.ej. en unit tests donde el composable se llama sin setup).
     */
    const currentRoute = () => {
        if (vm && vm.proxy && vm.proxy.$route) return vm.proxy.$route;
        return route;
    };

    /**
     * ID de temporada desde la URL. Es la fuente de verdad.
     */
    const listaId = computed(() => currentRoute().params.lista_id);

    /**
     * Objeto lista completo, resuelto desde store.getters.getListas
     * filtrando por el listaId de la URL. Retorna null si no hay match.
     */
    const listaActiva = computed(() => {
        const id = listaId.value;
        if (!id) return null;
        const listas = store.getters.getListas || [];
        const found = listas.find(l => String(l.id) === String(id));
        return found || null;
    });

    /**
     * Todas las listas disponibles (desde el store).
     */
    const listas = computed(() => store.getters.getListas || []);

    /**
     * Navega a la misma ruta actual con el nuevo :lista_id.
     * No-op si es la misma temporada. Si route.name no está definido,
     * cae a back.bases.index.
     *
     * @param {object} nuevaLista — Objeto lista con propiedad `id`
     * @returns {Promise<void>}
     */
    async function cambiarLista(nuevaLista) {
        if (!nuevaLista || !nuevaLista.id) return;
        // Comparamos contra el listaId reactivo (que ya usa currentRoute()).
        if (String(nuevaLista.id) === String(listaId.value)) return;

        const r = currentRoute();
        const targetName = r.name || 'back.bases.index';
        await router.push({
            name: targetName,
            params: { ...r.params, lista_id: nuevaLista.id }
        });
    }

    return {
        listaId,
        listaActiva,
        listas,
        cambiarLista
    };
}
